/* eslint-disable react-hooks/exhaustive-deps */
import React,{useState,useEffect,useCallback}from'react';
import{useNavigate,useSearchParams}from'react-router-dom';
import Layout from'../components/layout/Layout';
import PatientSearchInput from'../components/patient/PatientSearchInput';
import api from'../services/api';

const BASE=process.env.REACT_APP_API_URL;
const hdr=()=>({'Content-Type':'application/json','Authorization':`Bearer ${localStorage.getItem('accessToken')||''}`});

const INP='w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400';
const LBL='block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5';
const CARD='bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-5';
const CARD_HDR='px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white';
const CARD_BODY='p-5';

const F=({label,required,col2,children}:any)=>(
  <div className={col2?'col-span-2':''}>
    <label className={LBL}>{label}{required&&<span className="text-red-500 ml-0.5">*</span>}</label>
    {children}
  </div>
);

const DEF_FORM={
  // Core — ipd_admissions entity
  patientId:'',doctorId:'',departmentId:'',wardId:'',bedId:'',
  wardName:'',bedNumber:'',
  admissionDate:new Date().toISOString().split('T')[0],
  expectedDischargeDate:'',
  admissionType:'PLANNED',
  admissionReason:'',
  chiefComplaint:'',
  provisionalDiagnosis:'',
  diagnosis:'',
  medicalHistory:'',        // ← entity: medicalHistory
  surgicalHistory:'',       // ← entity: surgicalHistory
  familyHistory:'',         // ← entity: familyHistory
  allergyInfo:'',           // ← entity: allergyInfo (not knownAllergies)
  currentMedications:'',
  treatmentPlan:'',
  treatmentSummary:'',
  condition:'STABLE',       // ← entity: condition
  // Attendant — entity: attendantName/Phone/Relation
  attendantName:'',attendantPhone:'',attendantRelation:'',
  // Vitals at admission
  vitalsBP:'',vitalsHR:'',vitalTemp:'',vitalsSpO2:'',vitalsWeight:'',vitalsHeight:'',
  // Referral — entity: referredFrom, referralDoctor (stored as text, no FK)
  referredFrom:'',referralDoctor:'',
  // MLC — entity: mlcCase bool, policeStation
  mlcCase:false,mlcNumber:'',policeStation:'',policeInformed:false,firNumber:'',
  // Diet
  dietType:'REGULAR',
  // Insurance / billing — entity: depositAmount
  insuranceProvider:'',insurancePolicyNumber:'',tpaName:'',preAuthNumber:'',
  estimatedCost:'',depositAmount:'',depositMode:'CASH',depositReceiptNumber:'',
  estimatedDays:'',
  // Isolation — entity has isolationRequired, isolationType via specialInstructions
  isolationRequired:false,isolationType:'',
  // Code / consent
  codeStatus:'FULL',dnrOrder:false,consentForAdmission:false,consentForProcedures:false,
  // Notes
  admissionNotes:'',specialInstructions:'',
};

export default function IPDAdmissionPage(){
  const nav=useNavigate();
  const[sp]=useSearchParams();
  const[wards,setWards]=useState<any[]>([]);
  const[beds,setBeds]=useState<any[]>([]);
  const[doctors,setDoctors]=useState<any[]>([]);
  const[departments,setDepartments]=useState<any[]>([]);
  const[patient,setPatient]=useState<any>(null);
  const[saving,setSaving]=useState(false);
  const[msg,setMsg]=useState('');
  const[error,setError]=useState('');
  const[form,setForm]=useState({...DEF_FORM,patientId:sp.get('patientId')||''});
  const f=(k:string,v:any)=>setForm(x=>({...x,[k]:v}));

  const load=useCallback(async()=>{
    const[w,d,dep]=await Promise.all([
      fetch(`${BASE}/ipd/wards`,{headers:hdr()}).then(r=>r.json()).catch(()=>[]),
      api.getDoctors().catch(()=>({data:[]})),
      api.getDepartments?.().catch(()=>({data:[]})),
    ]);
    setWards(Array.isArray(w)?w:w?.data||w?.wards||[]);
    const docList=Array.isArray(d?.data)?d.data:d?.data?.data||d?.data?.doctors||d?.data?.users||[];
    setDoctors(docList);
    const depList=Array.isArray(dep?.data)?dep.data:dep?.data?.data||[];
    setDepartments(depList);
    // Load patient if passed in URL
    if(sp.get('patientId')){
      const r=await api.getPatient(sp.get('patientId')!).catch(()=>({data:null}));
      if(r?.data)setPatient(r.data);
    }
  },[]);

  useEffect(()=>{load();},[]);

  // Load available beds when ward changes
  useEffect(()=>{
    if(!form.wardId){setBeds([]);return;}
    fetch(`${BASE}/ipd/beds?wardId=${form.wardId}&status=AVAILABLE`,{headers:hdr()})
      .then(r=>r.json())
      .then(r=>{
        const list=Array.isArray(r)?r:r?.data||r?.beds||[];
        setBeds(list);
      }).catch(()=>setBeds([]));
    // Auto-fill wardName
    const ward=wards.find((w:any)=>w.id===form.wardId);
    if(ward)f('wardName',ward.name||ward.wardName||ward.wardCode||'');
  },[form.wardId]);

  const onPatientSelect=(p:any)=>{
    setPatient(p);
    f('patientId',p.id);
    // Pre-fill from patient record
    if(p.knownAllergies||p.known_allergies)f('allergyInfo',p.knownAllergies||p.known_allergies);
    if(p.bloodGroup||p.blood_group)f('condition','STABLE');
  };

  const onBedSelect=(bedId:string)=>{
    f('bedId',bedId);
    const bed=beds.find((b:any)=>b.id===bedId);
    if(bed)f('bedNumber',bed.bedNumber||bed.bed_number||'');
  };

  const submit=async()=>{
    if(!form.patientId){setError('Please select a patient');return;}
    if(!form.wardId){setError('Please select a ward');return;}
    if(!form.bedId){setError('Please select a bed');return;}
    if(!form.doctorId){setError('Please assign a treating doctor');return;}
    if(!form.chiefComplaint){setError('Chief complaint is required');return;}
    setSaving(true);setError('');
    try{
      // Use /ipd/admit — creates IPD number, marks bed occupied, creates patient_visit
      const payload={
        patientId:form.patientId,
        doctorId:form.doctorId,
        departmentId:form.departmentId||null,
        wardId:form.wardId,
        wardName:form.wardName||null,
        bedId:form.bedId,
        bedNumber:form.bedNumber||null,
        admissionDate:new Date(form.admissionDate).toISOString(),
        expectedDischargeDate:form.expectedDischargeDate?new Date(form.expectedDischargeDate).toISOString():null,
        admissionType:form.admissionType,
        admissionReason:form.admissionReason||null,
        chiefComplaint:form.chiefComplaint,
        provisionalDiagnosis:form.provisionalDiagnosis||null,
        diagnosis:form.diagnosis||null,
        medicalHistory:form.medicalHistory||null,
        surgicalHistory:form.surgicalHistory||null,
        familyHistory:form.familyHistory||null,
        allergyInfo:form.allergyInfo||null,
        treatmentPlan:form.treatmentPlan||null,
        condition:form.condition,
        // Attendant
        attendantName:form.attendantName||null,
        attendantPhone:form.attendantPhone||null,
        attendantRelation:form.attendantRelation||null,
        // Vitals
        vitalSigns:{
          bp:form.vitalsBP||null,hr:form.vitalsHR||null,
          temp:form.vitalTemp||null,spo2:form.vitalsSpO2||null,
          weight:form.vitalsWeight||null,height:form.vitalsHeight||null,
        },
        // MLC
        mlcCase:form.mlcCase,
        mlcNumber:form.mlcCase?form.mlcNumber:null,
        policeStation:form.mlcCase?form.policeStation:null,
        // Diet
        dietType:form.dietType,
        // Insurance
        insuranceProvider:form.insuranceProvider||null,
        insurancePolicyNumber:form.insurancePolicyNumber||null,
        tpaName:form.tpaName||null,
        preAuthNumber:form.preAuthNumber||null,
        estimatedCost:form.estimatedCost?+form.estimatedCost:null,
        depositAmount:form.depositAmount?+form.depositAmount:null,
        depositMode:form.depositMode||'CASH',
        estimatedDays:form.estimatedDays?+form.estimatedDays:null,
        // Isolation
        isolationRequired:form.isolationRequired,
        isolationType:form.isolationRequired?form.isolationType:null,
        // Consent
        consentForAdmission:form.consentForAdmission,
        consentForProcedures:form.consentForProcedures,
        codeStatus:form.codeStatus,
        dnrOrder:form.dnrOrder,
        // Notes
        admissionNotes:form.admissionNotes||null,
        specialInstructions:form.specialInstructions||null,
        referredFrom:form.referredFrom||null,
        referralDoctor:form.referralDoctor||null,
      };
      const r=await fetch(`${BASE}/ipd/admit`,{method:'POST',headers:hdr(),body:JSON.stringify(payload)});
      const res=await r.json();
      if(!r.ok)throw new Error(res.message||'Admission failed');
      setMsg(`✅ Patient admitted! IPD No: ${res.ipdNumber||res.data?.ipdNumber||res.ipd_number||'—'}`);
      setTimeout(()=>nav('/ipd'),2000);
    }catch(e:any){setError(e.message||'Admission failed. Please try again.');}
    setSaving(false);
  };

  const selectedWard=wards.find((w:any)=>w.id===form.wardId);
  const availBeds=beds.filter((b:any)=>b.status==='AVAILABLE');

  return(
    <Layout title="New IPD Admission" actions={
      <div className="flex gap-2">
        <button onClick={()=>nav('/ipd')} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2.5 px-5 rounded-xl border-2 border-slate-200 text-sm">← Back to IPD</button>
        <button onClick={submit} disabled={saving||!form.patientId||!form.bedId||!form.doctorId}
          className="bg-[#02357A] hover:bg-[#015CA6] text-white font-bold py-2.5 px-6 rounded-xl transition-all text-sm shadow-md disabled:opacity-50">
          {saving?'Admitting…':'✅ Admit Patient'}
        </button>
      </div>
    }>
      {/* Toast */}
      {error&&<div className="fixed top-5 right-5 z-50 px-5 py-3 rounded-2xl shadow-xl text-sm font-semibold bg-red-50 text-red-800 border border-red-200">{error}</div>}
      {msg&&<div className="fixed top-5 right-5 z-50 px-5 py-3 rounded-2xl shadow-xl text-sm font-semibold bg-green-50 text-green-800 border border-green-200">{msg}</div>}

      {/* Progress steps */}
      <div className="flex items-center gap-0 mb-6 overflow-x-auto">
        {['Patient','Admission','Bed','Clinical','Attendant','Insurance','Submit'].map((s,i)=>(
          <React.Fragment key={s}>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black ${i===0&&form.patientId?'bg-emerald-500 text-white':i===0?'bg-[#02357A] text-white':i<2?'bg-[#02357A] text-white':'bg-slate-100 text-slate-400'}`}>{i+1}</div>
              <span className="text-xs font-semibold text-slate-500">{s}</span>
            </div>
            {i<6&&<div className="h-px flex-1 bg-slate-200 mx-2 min-w-4"/>}
          </React.Fragment>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* LEFT COLUMN */}
        <div className="space-y-5">

          {/* Patient — no overflow-hidden so dropdown shows */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm mb-5">
            <div className={CARD_HDR}><h3 className="font-bold text-slate-800">👤 Patient</h3></div>
            <div className={CARD_BODY}>
              {patient?(
                <div className="bg-blue-50 rounded-2xl p-4 border border-blue-100 flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#02357A] to-[#039089] flex items-center justify-center text-white font-black text-lg flex-shrink-0">
                      {(patient.firstName||patient.first_name||'P')[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="font-black text-slate-900">{patient.firstName||patient.first_name} {patient.lastName||patient.last_name}</p>
                      <p className="text-xs font-mono text-[#02357A] mt-0.5">{patient.uhid}</p>
                      <div className="flex gap-2 mt-1 flex-wrap text-xs text-slate-500">
                        <span>{patient.gender||'—'}</span>
                        <span>·</span>
                        <span>{patient.dateOfBirth||patient.date_of_birth?new Date(patient.dateOfBirth||patient.date_of_birth).toLocaleDateString('en-IN'):'—'}</span>
                        {(patient.bloodGroup||patient.blood_group)&&<><span>·</span><span className="text-red-600 font-bold">🩸 {patient.bloodGroup||patient.blood_group}</span></>}
                        {(patient.phone||patient.mobile)&&<><span>·</span><span>📱 {patient.phone||patient.mobile}</span></>}
                      </div>
                    </div>
                  </div>
                  <button onClick={()=>{setPatient(null);f('patientId','');}} className="text-xs text-red-500 hover:text-red-700 font-semibold flex-shrink-0">Change</button>
                </div>
              ):(
                <PatientSearchInput selected={null} onSelect={onPatientSelect} onClear={()=>{}} label="" placeholder="Search patient by name, UHID or phone…"/>
              )}
            </div>
          </div>

          {/* Admission Details */}
          <div className={CARD}>
            <div className={CARD_HDR}><h3 className="font-bold text-slate-800">📋 Admission Details</h3></div>
            <div className={CARD_BODY}>
              <div className="grid grid-cols-2 gap-4">
                <F label="Admission Type" required>
                  <select value={form.admissionType} onChange={e=>f('admissionType',e.target.value)} className={INP}>
                    {['PLANNED','EMERGENCY','TRANSFER','REFERRAL','MATERNITY','SURGICAL','MEDICAL','PAEDIATRIC','DAY_CARE'].map(t=><option key={t}>{t}</option>)}
                  </select>
                </F>
                <F label="Admission Date" required>
                  <input type="date" value={form.admissionDate} onChange={e=>f('admissionDate',e.target.value)} className={INP}/>
                </F>
                <F label="Expected Discharge">
                  <input type="date" value={form.expectedDischargeDate} onChange={e=>f('expectedDischargeDate',e.target.value)} className={INP}/>
                </F>
                <F label="Estimated Stay (Days)">
                  <input type="number" value={form.estimatedDays} onChange={e=>f('estimatedDays',e.target.value)} placeholder="5" className={INP}/>
                </F>
                <F label="Department" required>
                  <select value={form.departmentId} onChange={e=>f('departmentId',e.target.value)} className={INP}>
                    <option value="">Select Department</option>
                    {departments.map((d:any)=><option key={d.id} value={d.id}>{d.name||d.department_name||d.departmentName}</option>)}
                    {departments.length===0&&['General Medicine','Surgery','Gynaecology','Orthopaedics','Paediatrics','Cardiology','Neurology','Urology','ENT','Ophthalmology','Psychiatry','Dermatology'].map(d=><option key={d} value={d}>{d}</option>)}
                  </select>
                </F>
                <F label="Treating Doctor" required>
                  <select value={form.doctorId} onChange={e=>f('doctorId',e.target.value)} className={INP}>
                    <option value="">Select Doctor</option>
                    {doctors.map((d:any)=>{
                      const fn=d.firstName||d.first_name||'';
                      const ln=d.lastName||d.last_name||'';
                      const name=`${fn} ${ln}`.trim();
                      const spec=d.specialization||d.department||'';
                      return<option key={d.id} value={d.id}>{name.startsWith('Dr.')?name:`Dr. ${name}`}{spec?` (${spec})`:''}</option>;
                    })}
                  </select>
                </F>
                <F label="Referral - From Hospital/Clinic" col2>
                  <input value={form.referredFrom} onChange={e=>f('referredFrom',e.target.value)} placeholder="e.g. City Clinic, PHC Name" className={INP}/>
                </F>
                <F label="Referring Doctor" col2>
                  <input value={form.referralDoctor} onChange={e=>f('referralDoctor',e.target.value)} placeholder="Dr. Name" className={INP}/>
                </F>
              </div>
            </div>
          </div>

          {/* Bed Allocation */}
          <div className={CARD}>
            <div className={CARD_HDR}><h3 className="font-bold text-slate-800">🛏️ Bed Allocation</h3></div>
            <div className={CARD_BODY}>
              <div className="space-y-4">
                <F label="Ward" required>
                  <select value={form.wardId} onChange={e=>f('wardId',e.target.value)} className={INP}>
                    <option value="">Select Ward</option>
                    {wards.map((w:any)=><option key={w.id} value={w.id}>{w.name||w.wardName||w.wardCode} ({w.wardType||w.ward_type||'General'})</option>)}
                  </select>
                </F>
                {selectedWard&&(
                  <div className="grid grid-cols-3 gap-3 text-center text-xs bg-blue-50 rounded-2xl p-3 border border-blue-100">
                    <div><p className="font-black text-slate-800 text-base">{selectedWard.totalBeds||0}</p><p className="text-slate-400">Total</p></div>
                    <div><p className="font-black text-emerald-600 text-base">{availBeds.length}</p><p className="text-slate-400">Available</p></div>
                    <div><p className="font-black text-red-600 text-base">{(selectedWard.occupiedBeds||0)}</p><p className="text-slate-400">Occupied</p></div>
                  </div>
                )}
                <F label="Available Bed" required>
                  <select value={form.bedId} onChange={e=>onBedSelect(e.target.value)} className={INP}>
                    <option value="">Select Bed</option>
                    {availBeds.map((b:any)=>{
                      const num=b.bedNumber||b.bed_number||'?';
                      const type=b.bedType||b.bed_type||'GENERAL';
                      const room=b.roomNumber||b.room_number;
                      const rate=b.dailyRate||b.daily_rate;
                      return<option key={b.id} value={b.id}>Bed {num} — {type}{room?` · Room ${room}`:''}${rate?` · ₹${rate}/day`:''}</option>;
                    })}
                  </select>
                  {form.wardId&&availBeds.length===0&&<p className="text-xs text-red-500 mt-1.5">⚠️ No available beds in this ward</p>}
                </F>
                <F label="Diet on Admission">
                  <select value={form.dietType} onChange={e=>f('dietType',e.target.value)} className={INP}>
                    {['REGULAR','DIABETIC','LOW_SALT','LIQUID','SOFT','NPO','HIGH_PROTEIN','VEGETARIAN','VEGAN','CARDIAC','RENAL'].map(d=><option key={d}>{d}</option>)}
                  </select>
                </F>
                <F label="Patient Condition at Admission">
                  <select value={form.condition} onChange={e=>f('condition',e.target.value)} className={INP}>
                    {['STABLE','CRITICAL','SERIOUS','FAIR','GOOD','DETERIORATING'].map(c=><option key={c}>{c}</option>)}
                  </select>
                </F>
              </div>
            </div>
          </div>

          {/* Admission Vitals */}
          <div className={CARD}>
            <div className={CARD_HDR}><h3 className="font-bold text-slate-800">❤️ Admission Vitals</h3></div>
            <div className={CARD_BODY}>
              <div className="grid grid-cols-3 gap-4">
                {[['BP (mmHg)','vitalsBP','120/80'],['Heart Rate (bpm)','vitalsHR','80'],['SpO₂ (%)','vitalsSpO2','98'],['Temperature (°F)','vitalTemp','98.6'],['Weight (kg)','vitalsWeight',''],['Height (cm)','vitalsHeight','']].map(([l,k,ph])=>(
                  <F key={k} label={l}>
                    <input value={(form as any)[k]} onChange={e=>f(k,e.target.value)} placeholder={ph} className={INP}/>
                  </F>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-5">

          {/* Clinical Information */}
          <div className={CARD}>
            <div className={CARD_HDR}><h3 className="font-bold text-slate-800">🩺 Clinical Information</h3></div>
            <div className={CARD_BODY}>
              <div className="space-y-4">
                <F label="Chief Complaint" required>
                  <textarea value={form.chiefComplaint} onChange={e=>f('chiefComplaint',e.target.value)} rows={2}
                    placeholder="Main presenting complaint…" className={`${INP} resize-y min-h-[60px]`}/>
                </F>
                <F label="Reason for Admission">
                  <textarea value={form.admissionReason} onChange={e=>f('admissionReason',e.target.value)} rows={2}
                    placeholder="Detailed reason for admission…" className={`${INP} resize-y min-h-[60px]`}/>
                </F>
                <F label="Provisional Diagnosis">
                  <input value={form.provisionalDiagnosis} onChange={e=>f('provisionalDiagnosis',e.target.value)} placeholder="Working diagnosis on admission" className={INP}/>
                </F>
                <F label="Initial Treatment Plan">
                  <textarea value={form.treatmentPlan} onChange={e=>f('treatmentPlan',e.target.value)} rows={2}
                    placeholder="Management plan…" className={`${INP} resize-y min-h-[60px]`}/>
                </F>
                <div className="grid grid-cols-2 gap-4">
                  <F label="Allergy Info">
                    <textarea value={form.allergyInfo} onChange={e=>f('allergyInfo',e.target.value)} rows={2}
                      placeholder="Drug/food allergies…" className={`${INP} resize-y min-h-[60px]`}/>
                  </F>
                  <F label="Chronic Conditions">
                    <textarea value={form.medicalHistory} onChange={e=>f('medicalHistory',e.target.value)} rows={2}
                      placeholder="DM, HTN, IHD, CKD…" className={`${INP} resize-y min-h-[60px]`}/>
                  </F>
                  <F label="Past Surgical History">
                    <textarea value={form.surgicalHistory} onChange={e=>f('surgicalHistory',e.target.value)} rows={2}
                      placeholder="Previous surgeries…" className={`${INP} resize-y min-h-[60px]`}/>
                  </F>
                  <F label="Family History">
                    <textarea value={form.familyHistory} onChange={e=>f('familyHistory',e.target.value)} rows={2}
                      placeholder="DM, HTN, Cancer in family…" className={`${INP} resize-y min-h-[60px]`}/>
                  </F>
                </div>
                <F label="Current Medications">
                  <textarea value={form.currentMedications} onChange={e=>f('currentMedications',e.target.value)} rows={2}
                    placeholder="Ongoing home medications…" className={`${INP} resize-y min-h-[60px]`}/>
                </F>
              </div>
            </div>
          </div>

          {/* Attendant / Guardian */}
          <div className={CARD}>
            <div className={CARD_HDR}><h3 className="font-bold text-slate-800">👨‍👩‍👧 Attendant / Guardian</h3></div>
            <div className={CARD_BODY}>
              <div className="grid grid-cols-2 gap-4">
                <F label="Name">
                  <input value={form.attendantName} onChange={e=>f('attendantName',e.target.value)} placeholder="Full name" className={INP}/>
                </F>
                <F label="Phone">
                  <input value={form.attendantPhone} onChange={e=>f('attendantPhone',e.target.value)} placeholder="Mobile number" className={INP}/>
                </F>
                <F label="Relation" col2>
                  <select value={form.attendantRelation} onChange={e=>f('attendantRelation',e.target.value)} className={INP}>
                    <option value="">Select Relation</option>
                    {['SPOUSE','FATHER','MOTHER','SON','DAUGHTER','SIBLING','FRIEND','GUARDIAN','OTHER'].map(r=><option key={r}>{r}</option>)}
                  </select>
                </F>
              </div>
            </div>
          </div>

          {/* Insurance & Billing */}
          <div className={CARD}>
            <div className={CARD_HDR}><h3 className="font-bold text-slate-800">💳 Insurance & Initial Billing</h3></div>
            <div className={CARD_BODY}>
              <div className="grid grid-cols-2 gap-4">
                <F label="Insurance Provider">
                  <input value={form.insuranceProvider} onChange={e=>f('insuranceProvider',e.target.value)} placeholder="Star Health, CGHS, ESI…" className={INP}/>
                </F>
                <F label="Policy Number">
                  <input value={form.insurancePolicyNumber} onChange={e=>f('insurancePolicyNumber',e.target.value)} placeholder="Policy no." className={INP}/>
                </F>
                <F label="TPA Name">
                  <input value={form.tpaName} onChange={e=>f('tpaName',e.target.value)} placeholder="Medi-Assist, Vidal…" className={INP}/>
                </F>
                <F label="Pre-Auth Number">
                  <input value={form.preAuthNumber} onChange={e=>f('preAuthNumber',e.target.value)} placeholder="Pre-authorization no." className={INP}/>
                </F>
                <F label="Estimated Cost (₹)">
                  <input type="number" value={form.estimatedCost} onChange={e=>f('estimatedCost',e.target.value)} placeholder="0" className={INP}/>
                </F>
                <F label="Deposit Amount (₹)">
                  <input type="number" value={form.depositAmount} onChange={e=>f('depositAmount',e.target.value)} placeholder="0" className={INP}/>
                </F>
                <F label="Deposit Mode">
                  <select value={form.depositMode} onChange={e=>f('depositMode',e.target.value)} className={INP}>
                    {['CASH','CARD','UPI','NEFT','RTGS','CHEQUE'].map(m=><option key={m}>{m}</option>)}
                  </select>
                </F>
                <F label="Receipt Number">
                  <input value={form.depositReceiptNumber} onChange={e=>f('depositReceiptNumber',e.target.value)} placeholder="Receipt / transaction no." className={INP}/>
                </F>
              </div>
            </div>
          </div>

          {/* MLC */}
          <div className={CARD}>
            <div className={CARD_HDR}>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form.mlcCase} onChange={e=>f('mlcCase',e.target.checked)} className="w-4 h-4 accent-red-600"/>
                <h3 className="font-bold text-red-700">⚖️ Medico-Legal Case (MLC)</h3>
              </label>
            </div>
            {form.mlcCase&&(
              <div className={CARD_BODY}>
                <div className="grid grid-cols-2 gap-4">
                  <F label="MLC Number"><input value={form.mlcNumber} onChange={e=>f('mlcNumber',e.target.value)} placeholder="MLC-2026-001" className={INP}/></F>
                  <F label="Police Station"><input value={form.policeStation} onChange={e=>f('policeStation',e.target.value)} placeholder="Police station name" className={INP}/></F>
                  <F label="FIR Number"><input value={form.firNumber} onChange={e=>f('firNumber',e.target.value)} placeholder="FIR no. (if available)" className={INP}/></F>
                  <div className="flex items-center gap-3 pt-5">
                    <input type="checkbox" checked={form.policeInformed} onChange={e=>f('policeInformed',e.target.checked)} className="w-4 h-4 accent-red-600"/>
                    <label className="text-sm font-semibold text-slate-700">Police Informed</label>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Isolation */}
          <div className={CARD}>
            <div className={CARD_HDR}>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form.isolationRequired} onChange={e=>f('isolationRequired',e.target.checked)} className="w-4 h-4 accent-[#02357A]"/>
                <h3 className="font-bold text-slate-800">🔒 Isolation Required</h3>
              </label>
            </div>
            {form.isolationRequired&&(
              <div className={CARD_BODY}>
                <F label="Isolation Type">
                  <select value={form.isolationType} onChange={e=>f('isolationType',e.target.value)} className={INP}>
                    <option value="">Select Type</option>
                    {['CONTACT','DROPLET','AIRBORNE','REVERSE','NEUTROPENIC','STRICT'].map(t=><option key={t}>{t}</option>)}
                  </select>
                </F>
              </div>
            )}
          </div>

          {/* Code Status & Consent */}
          <div className={CARD}>
            <div className={CARD_HDR}><h3 className="font-bold text-slate-800">📄 Code Status & Consent</h3></div>
            <div className={CARD_BODY}>
              <div className="space-y-3">
                <F label="Code Status">
                  <select value={form.codeStatus} onChange={e=>f('codeStatus',e.target.value)} className={INP}>
                    {['FULL','DNR','COMFORT_CARE','LIMITED'].map(c=><option key={c}>{c}</option>)}
                  </select>
                </F>
                <div className="grid grid-cols-2 gap-3 pt-2">
                  {[['consentForAdmission','Consent for Admission'],['consentForProcedures','Consent for Procedures'],['dnrOrder','DNR Order Signed']].map(([k,l])=>(
                    <label key={k} className="flex items-center gap-2.5 cursor-pointer p-3 rounded-xl border-2 border-slate-100 hover:border-slate-200 transition-all">
                      <input type="checkbox" checked={(form as any)[k]} onChange={e=>f(k,e.target.checked)} className="w-4 h-4 accent-[#02357A]"/>
                      <span className="text-sm font-medium text-slate-700">{l}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Special Instructions */}
          <div className={CARD}>
            <div className={CARD_HDR}><h3 className="font-bold text-slate-800">📝 Admission Notes</h3></div>
            <div className={CARD_BODY}>
              <div className="space-y-4">
                <F label="Special Instructions">
                  <textarea value={form.specialInstructions} onChange={e=>f('specialInstructions',e.target.value)} rows={2}
                    placeholder="Isolation, fall risk, diet notes, monitoring…" className={`${INP} resize-y min-h-[60px]`}/>
                </F>
                <F label="Admission Notes">
                  <textarea value={form.admissionNotes} onChange={e=>f('admissionNotes',e.target.value)} rows={2}
                    placeholder="Additional nursing/doctor notes…" className={`${INP} resize-y min-h-[60px]`}/>
                </F>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Submit */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 mt-2 flex items-center justify-between">
        <div>
          {!form.patientId&&<p className="text-xs text-red-500 font-medium">⚠️ Select patient</p>}
          {form.patientId&&!form.wardId&&<p className="text-xs text-amber-600 font-medium">⚠️ Select ward and bed</p>}
          {form.patientId&&form.wardId&&!form.bedId&&<p className="text-xs text-amber-600 font-medium">⚠️ Select a bed</p>}
          {form.patientId&&form.bedId&&!form.doctorId&&<p className="text-xs text-amber-600 font-medium">⚠️ Assign a treating doctor</p>}
          {form.patientId&&form.bedId&&form.doctorId&&<p className="text-xs text-emerald-600 font-bold">✅ Ready to admit</p>}
        </div>
        <div className="flex gap-3">
          <button onClick={()=>nav('/ipd')} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2.5 px-6 rounded-xl border-2 border-slate-200 text-sm">Cancel</button>
          <button onClick={submit} disabled={saving||!form.patientId||!form.bedId||!form.doctorId||!form.chiefComplaint}
            className="bg-[#02357A] hover:bg-[#015CA6] text-white font-bold py-2.5 px-8 rounded-xl transition-all shadow-md shadow-[#02357A]/20 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
            {saving?'Admitting…':'✅ Admit Patient'}
          </button>
        </div>
      </div>
    </Layout>
  );
}
